/**
 * Runs `worker` over every item in `items`, never allowing more than `limit` calls to be
 * in flight at once. Not `Promise.all` (unbounded - risks Graph 429 throttling on large folder
 * trees) and not fully sequential (much slower for many small files).
 *
 * A rejecting worker call does not stop other items from being processed - every item still gets
 * its turn. Once every item has settled, the first error encountered (if any) is re-thrown so a
 * caller that wants "fail the whole batch" behavior still can; a caller doing its own per-item
 * try/catch inside `worker` (this file-manager's upload orchestrator does) never sees a rejection
 * from this function at all.
 */
export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  let firstError: unknown;
  let hasError = false;

  const runNext = async (): Promise<void> => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      try {
        await worker(items[index], index);
      } catch (error) {
        if (!hasError) {
          hasError = true;
          firstError = error;
        }
      }
    }
  };

  const runnerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: runnerCount }, runNext));

  if (hasError) throw firstError;
}
